package top.wcpe.allincore.ui.runtime.internal.time

import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.atTime
import kotlinx.datetime.format.DateTimeComponents
import kotlinx.datetime.format.FormatStringsInDatetimeFormats
import kotlinx.datetime.format.byUnicodePattern
import kotlinx.datetime.isoDayNumber
import kotlinx.datetime.number
import kotlinx.datetime.plus
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime
import kotlinx.datetime.until
import kotlin.time.Instant

// JS：委托 kotlinx-datetime（依赖仅进 jsMain）。日历运算（月加减钳制 / ISO 周）由库保证与 JVM 一致；
// pattern 格式化经 byUnicodePattern 覆盖常用 Unicode pattern 子集，不支持的指令抛 IllegalArgumentException。

/** 当前系统时区的本地日期时间快照（时间源统一走 UiTime shim，保持全库单一时钟）。 */
private fun nowLocal(): LocalDateTime =
    Instant.fromEpochMilliseconds(currentTimeMillis()).toLocalDateTime(TimeZone.currentSystemDefault())

internal actual fun localTimeSecond(): Int = nowLocal().second

internal actual fun localTimeMinute(): Int = nowLocal().minute

internal actual fun localTimeHour(): Int = nowLocal().hour

internal actual fun localDateIso(): String = nowLocal().date.toString()

internal actual fun localDateYear(): Int = nowLocal().year

internal actual fun localDateMonth(): Int = nowLocal().month.number

internal actual fun localDateDay(): Int = nowLocal().day

internal actual fun localDateDayOfWeek(): Int = nowLocal().dayOfWeek.isoDayNumber

internal actual fun localDateWeekOfYear(): Int = isoWeekOfYear(nowLocal().date)

internal actual fun localDateDayOfYear(): Int = nowLocal().dayOfYear

@OptIn(FormatStringsInDatetimeFormats::class)
internal actual fun formatEpochMillis(epochMillis: Long, pattern: String): String {
    val fmt = try {
        LocalDateTime.Format { byUnicodePattern(pattern) }
    } catch (e: Exception) {
        // byUnicodePattern 对不支持的指令可能抛 UnsupportedOperationException，统一收口为 IAE 契约
        throw IllegalArgumentException(e.message, e)
    }
    return fmt.format(Instant.fromEpochMilliseconds(epochMillis).toLocalDateTime(TimeZone.currentSystemDefault()))
}

@OptIn(FormatStringsInDatetimeFormats::class)
internal actual fun parseToEpochMillisOrNull(text: String, pattern: String): Long? {
    val fmt = try {
        DateTimeComponents.Format { byUnicodePattern(pattern) }
    } catch (e: Exception) {
        throw IllegalArgumentException(e.message, e)
    }
    val components = try {
        fmt.parse(text)
    } catch (ignored: Exception) {
        return null
    }
    // 对齐 JVM 降级路径：优先完整日期时间，缺时间部分则按纯日期补当天 00:00
    val ldt = try {
        components.toLocalDateTime()
    } catch (ignored: Exception) {
        try {
            components.toLocalDate().atTime(0, 0)
        } catch (ignored2: Exception) {
            return null
        }
    }
    return ldt.toInstant(TimeZone.currentSystemDefault()).toEpochMilliseconds()
}

internal actual fun addToEpochMillis(epochMillis: Long, amount: Long, unit: CalendarUnit): Long =
    Instant.fromEpochMilliseconds(epochMillis)
        .plus(amount, unit.toDateTimeUnit(), TimeZone.currentSystemDefault())
        .toEpochMilliseconds()

internal actual fun diffEpochMillis(endMillis: Long, startMillis: Long, unit: CalendarUnit): Long =
    Instant.fromEpochMilliseconds(startMillis)
        .until(Instant.fromEpochMilliseconds(endMillis), unit.toDateTimeUnit(), TimeZone.currentSystemDefault())

private fun CalendarUnit.toDateTimeUnit(): DateTimeUnit = when (this) {
    CalendarUnit.MILLIS -> DateTimeUnit.MILLISECOND
    CalendarUnit.SECONDS -> DateTimeUnit.SECOND
    CalendarUnit.MINUTES -> DateTimeUnit.MINUTE
    CalendarUnit.HOURS -> DateTimeUnit.HOUR
    CalendarUnit.DAYS -> DateTimeUnit.DAY
    CalendarUnit.WEEKS -> DateTimeUnit.WEEK
    CalendarUnit.MONTHS -> DateTimeUnit.MONTH
    CalendarUnit.YEARS -> DateTimeUnit.YEAR
}

/** ISO-8601 周数：kotlinx-datetime 无直接 API，按标准算法手算（与 java.time IsoFields 语义一致）。 */
private fun isoWeekOfYear(date: LocalDate): Int {
    val week = (date.dayOfYear - date.dayOfWeek.isoDayNumber + 10) / 7
    if (week < 1) return isoWeeksInYear(date.year - 1)
    if (week > isoWeeksInYear(date.year)) return 1
    return week
}

/** ISO 周基准年的周数（52 或 53）：1 月 1 日为周四，或闰年且 1 月 1 日为周三时为 53 周。 */
private fun isoWeeksInYear(year: Int): Int {
    val jan1Dow = LocalDate(year, 1, 1).dayOfWeek.isoDayNumber
    val leap = (year % 4 == 0 && year % 100 != 0) || year % 400 == 0
    return if (jan1Dow == 4 || (leap && jan1Dow == 3)) 53 else 52
}
